import * as React from "react";
import { injectIntl, WrappedComponentProps } from "react-intl";
import PulseLoadingDot from "common/components/pulseLoadingDot";
import FeedHeader from "../../components/feedHeader";
import TrendingSectionSlide from "../../components/trendingSectionSlide";
import TrendingContentSlide from "../../components/trendingContentSlide";
import InfiniteScroller from "@vingle/wtf-sdk/components/infiniteScroller";
import CompactCard from "common/components/compactCard";
import translateAppURLToURL from "@vingle/wtf-sdk/helpers/translateAppURLToURL";
import PACKS from "common/icons/packs";
import InterestChart from "app/discover/pages/interestChart";
import { IconWrapper } from "common/icons";
import NeowizImportant, {
  NEOWIZ_AD_PAGES,
} from "app/postShow/components/neowiz";
import { VingleURL } from "@vingle/wtf-sdk/url";
import { GridLayout, Grid560, Grid320 } from "common/components/gridLayout";
import getPreviewContent from "app/discover/helpers/getPreviewContent";

const styles = require("./discover.scss");

interface IProps extends WrappedComponentProps {
  sections: Vingle.Discover.ITrendingSection[];
  selectedSectionId: null | string;
  showAd: boolean;
  categories: Vingle.Discover.IDiscoverCategory[];
  feed: Vingle.IPaginatedData<Vingle.Discover.IDiscoverFeedContent>;
  categoryErrorReason: Vingle.Discover.CategoryErrorReason;
  isDiscoverCategoriesLoading: boolean;
  isDiscoverFeedLoading: boolean;
  isTrendingSectionsLoading: boolean;
  handleLoadFeedData: () => void;
  onClickSection: (
    section: Vingle.Discover.ITrendingSection,
    event: React.MouseEvent,
  ) => void;
}

class DiscoverComponent extends React.PureComponent<IProps> {
  public render() {
    const {
      sections,
      categories,
      feed,
      isDiscoverCategoriesLoading,
      isDiscoverFeedLoading,
      isTrendingSectionsLoading,
      categoryErrorReason,
      selectedSectionId,
      onClickSection,
    } = this.props;
    if (
      (!categories.length && isDiscoverCategoriesLoading) ||
      (!feed.data.contents.length && isDiscoverFeedLoading) ||
      (!sections.length && isTrendingSectionsLoading)
    ) {
      return (
        <div className={styles.pulseLoadingDotWrapper}>
          <PulseLoadingDot />
        </div>
      );
    }

    return (
      <div className={styles.container}>
        <div className={styles.slide}>
          <div className={styles.wrapper}>
            <TrendingSectionSlide
              sections={sections}
              selectedSectionId={selectedSectionId}
              onClickSection={onClickSection}
            />
          </div>
        </div>
        {this.renderAd()}
        <GridLayout>
          <Grid560>
            {categoryErrorReason
              ? this.renderCategoryErrorReason()
              : this.renderContent()}
          </Grid560>
          <Grid320 element="aside" className={styles.aside}>
            <div className={styles.interestChart}>
              <InterestChart />
            </div>
          </Grid320>
        </GridLayout>
      </div>
    );
  }

  private readonly renderAd = () => {
    if (!this.props.showAd) {
      return null;
    }
    return (
      <NeowizImportant
        page={NEOWIZ_AD_PAGES.DISCOVER}
        wrapperClassName={styles.adWrapper}
      />
    );
  };

  private readonly renderContent = () => {
    const { categories, feed, handleLoadFeedData, intl } = this.props;
    return (
      <React.Fragment>
        {categories.map((feedContent, index) => {
          return (
            <section className={styles.section} key={feedContent.title}>
              <TrendingContentSlide
                feedContent={feedContent}
                categoryPosition={index}
              />
            </section>
          );
        })}
        <section className={styles.section}>
          <FeedHeader title={feed.data.title} />
          <InfiniteScroller
            loadMore={handleLoadFeedData}
            itemLength={feed.data.contents.length}
            loader={
              <footer className={styles.footer}>
                <PulseLoadingDot />
              </footer>
            }
            paging={feed.paging}
            threshold={750}
          >
            {feed.data.contents.map((item, index) => (
              <CompactCardItem key={item.url} item={item} position={index} />
            ))}
            {!feed.paging.after && (
              <footer className={styles.footer}>
                {this.renderOtherMessage(
                  intl.formatMessage({ id: "discover.reached_the_end" }),
                )}
              </footer>
            )}
          </InfiniteScroller>
        </section>
      </React.Fragment>
    );
  };

  private readonly renderCategoryErrorReason = () => {
    const { categoryErrorReason, intl } = this.props;
    if (!categoryErrorReason) {
      return null;
    }
    let message = "";
    switch (categoryErrorReason.code) {
      case "NOT_ENOUGH_INFORMATION": {
        message = intl.formatMessage({
          id: "discover.not_enough_information",
        });
        break;
      }
    }
    return (
      <section className={styles.section}>
        <FeedHeader title={intl.formatMessage({ id: "signup.discover" })} />
        <footer className={styles.footer} style={{ marginTop: "30px" }}>
          {this.renderOtherMessage(message)}
        </footer>
      </section>
    );
  };

  private readonly renderOtherMessage = (message: string) => {
    return (
      <>
        <IconWrapper
          icon={PACKS.GENERAL.MORE_LIKE}
          className={styles.otherMessageIcon}
        />
        <p className={styles.otherMessage}>{message}</p>
      </>
    );
  };
}

const CompactCardItem = ({
  item,
  position,
}: {
  item: Vingle.Discover.IDiscoverContent;
  position: number;
}) => {
  const texts = React.useMemo(() => getPreviewContent(item), [item]);
  return (
    <CompactCard
      link={{
        pathname: translateAppURLToURL(item.url),
        state: {
          modal: true,
        },
      }}
      title={item.title}
      content={texts}
      description={item.desc.text}
      media={{
        type: "image",
        src: item.thumbnail,
      }}
      trackTicket={{
        contentType: "url",
        contentId: item.url,
        position,
        referralUrl: new VingleURL.DiscoverOthers().toSchema(),
      }}
    />
  );
};

export default injectIntl(DiscoverComponent);
